import { useParams, Navigate } from "react-router-dom";
import { ArrowLeft, ArrowUpRight } from "lucide-react";
import { useLang, UI } from "@/i18n/lang";
import { PROJECTS, CATEGORIES } from "@/content/site";
import { Reveal, ParallaxImage } from "@/lib/motion";
import { Seo, ORIGIN } from "@/components/Seo";
import { LocaleLink as Link, useLocaleRoute, localizePath, coreAlternates } from "@/i18n/routes";

export default function CaseStudy({ locale = "de" }) {
  const { slug } = useParams();
  const { t } = useLang();
  useLocaleRoute(locale, localizePath(`/work/${slug}`, locale === "de" ? "en" : "de"));
  const idx = PROJECTS.findIndex((p) => p.slug === slug);
  if (idx === -1) return <Navigate to={localizePath("/work", locale)} replace />;
  const project = PROJECTS[idx];
  const next = PROJECTS[(idx + 1) % PROJECTS.length];
  const cat = CATEGORIES.find((c) => c.id === project.category);

  const seoTitle = `${project.title} — ${cat ? cat.label[locale] : "Projekt"} | WERKVERDUNG`;
  const seoDesc = project.short[locale];
  const seoPath = localizePath(`/work/${slug}`, locale);
  const jsonLd = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "CreativeWork",
        name: project.title,
        description: seoDesc,
        image: project.image,
        url: ORIGIN + seoPath,
        dateCreated: String(project.year),
        author: { "@type": "Person", name: "Florian Teodorescu" },
        creator: { "@type": "Organization", name: "WERKVERDUNG", url: ORIGIN },
      },
      {
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Home", item: ORIGIN + localizePath("/", locale) },
          { "@type": "ListItem", position: 2, name: locale === "de" ? "Projekte" : "Work", item: ORIGIN + localizePath("/work", locale) },
          { "@type": "ListItem", position: 3, name: project.title, item: ORIGIN + seoPath },
        ],
      },
    ],
  };

  return (
    <article data-testid="case-study-page">
      <Seo
        title={seoTitle}
        description={seoDesc}
        path={seoPath}
        lang={locale}
        image={project.image}
        type="article"
        alternates={coreAlternates(`/work/${slug}`)}
        jsonLd={jsonLd}
      />
      {/* Header */}
      <section className="pt-32 md:pt-40 pb-12">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12 lg:px-20">
          <Link to="/work" data-testid="back-to-work" className="inline-flex items-center gap-2 font-mono text-[12px] uppercase tracking-widest text-stone hover:text-ink transition-colors mb-12">
            <ArrowLeft size={14} /> {t(UI.cta.back)}
          </Link>
          <Reveal>
            <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-bronze">
              {cat ? t(cat.label) : ""} · {project.year}
            </span>
            <h1 className="mt-5 font-serif text-5xl md:text-7xl lg:text-8xl tracking-tight text-ink leading-[0.98]">
              {project.title}
            </h1>
            <p className="mt-6 max-w-2xl text-lg text-stone">{t(project.short)}</p>
          </Reveal>
        </div>
      </section>

      {/* Hero image */}
      <section className="mb-20 md:mb-28">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12 lg:px-20">
          <ParallaxImage src={project.image} alt={project.title} className="w-full aspect-[16/9] bg-secondary" strength={30} />
        </div>
      </section>

      {/* Meta grid */}
      <section className="pb-20 md:pb-28">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12 lg:px-20">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8">
            <div className="lg:col-span-4 space-y-10">
              <MetaBlock label={t(UI.services?.build) || "Type"} value={t(project.type)} />
              <MetaBlock label={t(UI.caseStudy.built)} value={t(project.role)} />
              <div>
                <p className="font-mono text-[11px] uppercase tracking-widest text-bronze mb-3">{t(UI.caseStudy.tech)}</p>
                <div className="flex flex-wrap gap-2">
                  {project.tech.map((tech) => (
                    <span key={tech} className="rounded-full border border-border px-3.5 py-1.5 text-[12px] text-stone">{tech}</span>
                  ))}
                </div>
              </div>
            </div>

            <div className="lg:col-span-7 lg:col-start-6 space-y-12">
              <Reveal><Section title={t(UI.caseStudy.overview)} body={t(project.overview)} /></Reveal>
              <Reveal><Section title={t(UI.caseStudy.problem)} body={t(project.problem)} /></Reveal>
              <Reveal><Section title={t(UI.caseStudy.approach)} body={t(project.approach)} /></Reveal>
              <Reveal>
                <div>
                  <h2 className="font-serif text-3xl text-ink mb-5">{t(UI.caseStudy.built)}</h2>
                  <ul className="space-y-3">
                    {t(project.built).map((item, i) => (
                      <li key={i} className="flex items-baseline gap-4 border-b border-border pb-3">
                        <span className="font-mono text-xs text-bronze">0{i + 1}</span>
                        <span className="text-base text-ink">{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      {/* Gallery */}
      {project.gallery?.length > 0 && (
        <section className="pb-20 md:pb-28">
          <div className="max-w-[1440px] mx-auto px-6 md:px-12 lg:px-20">
            <p className="font-mono text-[11px] uppercase tracking-widest text-bronze mb-8">{t(UI.caseStudy.gallery)}</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {project.gallery.map((src, i) => (
                <Reveal key={i} delay={(i % 2) * 0.08}>
                  <div className="overflow-hidden bg-secondary aspect-[4/3]">
                    <img src={src} alt={`${project.title} ${i + 1}`} loading="lazy" className="w-full h-full object-cover" />
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Result */}
      <section className="py-20 md:py-28 bg-secondary">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12 lg:px-20">
          <div className="max-w-3xl">
            <p className="font-mono text-[11px] uppercase tracking-widest text-bronze mb-6">{t(UI.caseStudy.result)}</p>
            <p className="font-serif text-3xl md:text-4xl lg:text-5xl leading-[1.15] tracking-tight text-ink">
              {t(project.result)}
            </p>
            {project.liveLink ? (
              <a href={project.liveLink} target="_blank" rel="noreferrer" className="mt-10 inline-flex items-center gap-2 rounded-full bg-bronze text-paper px-8 py-4 text-sm font-semibold hover:bg-[#f0be6e] transition-colors">
                {t(UI.cta.liveProduct)} <ArrowUpRight size={16} />
              </a>
            ) : (
              <span className="mt-10 inline-flex items-center gap-2 rounded-full border border-border px-8 py-4 text-sm font-medium text-stone/70">
                {t(UI.cta.liveSoon)}
              </span>
            )}
          </div>
        </div>
      </section>

      {/* Next project */}
      <section className="border-t border-border">
        <Link to={`/work/${next.slug}`} data-testid="next-project" className="group block max-w-[1440px] mx-auto px-6 md:px-12 lg:px-20 py-20 md:py-28">
          <p className="font-mono text-[11px] uppercase tracking-widest text-bronze mb-4">{t(UI.caseStudy.next)}</p>
          <div className="flex items-center justify-between gap-6">
            <h2 className="font-serif text-4xl md:text-6xl lg:text-7xl tracking-tight text-ink group-hover:text-bronze transition-colors duration-300">
              {next.title}
            </h2>
            <ArrowUpRight size={40} className="text-ink group-hover:text-bronze group-hover:translate-x-1 group-hover:-translate-y-1 transition-all duration-300 shrink-0" />
          </div>
        </Link>
      </section>
    </article>
  );
}

const MetaBlock = ({ label, value }) => (
  <div>
    <p className="font-mono text-[11px] uppercase tracking-widest text-bronze mb-2">{label}</p>
    <p className="text-base text-ink">{value}</p>
  </div>
);

const Section = ({ title, body }) => (
  <div>
    <h2 className="font-serif text-3xl text-ink mb-4">{title}</h2>
    <p className="text-base md:text-lg leading-relaxed text-stone">{body}</p>
  </div>
);
